// Die Darstellung der Bestenliste — **reine Funktionen, die HTML liefern.**
//
// Dasselbe Muster wie `ui/help.ts` und aus demselben Grund: als Template-Block
// in `panels.ts` waere dieser Text ohne DOM nicht pruefbar und wuerde die
// Coverage druecken, statt sie zu heben (Gate FG-08). `panels.ts` haelt nur
// noch den Zustand, den Abruf und die Klicks.
//
// Hier steht kein `document`, kein `fetch` und kein Zustand — das ist die
// Bedingung dafuer, dass ein Test die Ausgabe einfach vergleichen kann.

import { escapeHtml, fmt } from '../core/format';
import type { RankingBoard, RankingPayload, RankingRow, RankingSpan } from '../net/api';

/**
 * Wie oft die offene Bestenliste nachlaedt.
 *
 * Ein `GET` alle zwanzig Sekunden, und nur solange der Reiter offen ist.
 * Haeufiger waere eine Abfrage ueber alle Spielstaende je Sekunde, fuer eine
 * Zahl, die sich in dieser Zeit kaum aendert.
 */
export const RANK_RELOAD_MS = 20000;

export const RANK_BOARDS: ReadonlyArray<readonly [RankingBoard, string]> = [
  ['yield', 'Ausbeute'],
  ['progress', 'Fortschritt'],
];

export const RANK_SPANS: ReadonlyArray<readonly [RankingSpan, string]> = [
  ['all', 'Insgesamt'],
  ['week', 'Diese Woche'],
];

/** Das Attribut, an dem `panels.ts` die Umschalter wiederfindet. */
export const RANK_ATTR = 'data-rank';

/**
 * „vor 12 s" — das Alter einer geladenen Liste.
 *
 * Bewusst nicht `timeStr`: das formatiert Spieldauern und schriebe „0:12",
 * was hier nach einer Rundenzeit aussaehe.
 */
export function agoStr(ms: number): string {
  const sekunden = Math.max(0, Math.floor(ms / 1000));
  if (sekunden < 60) return `${sekunden} s`;
  const minuten = Math.floor(sekunden / 60);
  return minuten < 60 ? `${minuten} min` : `${Math.floor(minuten / 60)} h`;
}

/** Beide Umschaltgruppen — Wertung links, Zeitraum rechts. */
export function rankTabsHtml(board: RankingBoard, span: RankingSpan): string {
  return gruppeHtml('board', board, RANK_BOARDS) +
    '<span class="subtab-gap"></span>' +
    gruppeHtml('span', span, RANK_SPANS);
}

function gruppeHtml(
  gruppe: 'board' | 'span',
  aktiv: string,
  eintraege: ReadonlyArray<readonly [string, string]>,
): string {
  return eintraege
    .map(([id, label]) => `<button class="subtab ${id === aktiv ? 'active' : ''}" ` +
      `${RANK_ATTR}="${gruppe}" data-rank-value="${id}">${escapeHtml(label)}</button>`)
    .join('');
}

/** Was eine Zeile anzeigt — bei der Ausbeute eine Zahl, sonst Stufe und Level. */
export function rankScoreText(row: RankingRow, board: RankingBoard): string {
  return board === 'yield' ? fmt(row.score) : `Stufe ${row.built} · Level ${row.level}`;
}

export function rankRowHtml(row: RankingRow, board: RankingBoard): string {
  // Der Anzeigename kommt vom Server und damit von einem anderen Menschen
  // (`oberflaeche.md`).
  return `<div class="rank-row${row.me ? ' me' : ''}">` +
    `<span class="rank-pos">${row.rank}</span>` +
    `<span class="rank-name">${escapeHtml(row.name)}</span>` +
    `<b class="rank-score">${escapeHtml(rankScoreText(row, board))}</b></div>`;
}

export interface RankingView {
  data: RankingPayload | null;
  board: RankingBoard;
  /** Der letzte Abruf schlug fehl — der Reiter zeigt trotzdem den alten Stand. */
  failed: boolean;
  /** Alter der Daten in Millisekunden. */
  age: number;
}

/**
 * Der Koerper des Reiters — Liste, Leerfall oder Hinweis.
 *
 * Das Alter steht **immer** dabei: eine Liste ohne Zeitangabe sieht bei jedem
 * Wert nach „jetzt" aus, und bei Verbindungsabbruch bliebe sie stumm falsch
 * stehen.
 */
export function rankingBodyHtml(view: RankingView): string {
  const { data, board, failed, age } = view;
  if (data === null) {
    return failed
      ? '<p class="rank-note">Die Bestenliste ist gerade nicht erreichbar. ' +
        'Das Spiel läuft weiter — es wird weiter versucht.</p>'
      : '<p class="rank-note">Bestenliste wird geladen …</p>';
  }
  if (data.rows.length === 0) {
    return '<p class="rank-note">Hier hat noch niemand etwas abgebaut. ' +
      'Der erste Brocken genügt.</p>';
  }

  // Gewertet wird nur, wer etwas abgebaut hat — sonst stuenden hinter den
  // echten Plaetzen lauter Nullen. Wer deshalb fehlt, erfaehrt es hier, statt
  // sich in einer Liste zu suchen, in der er nicht vorkommt.
  const fehlt = !data.rows.some((row) => row.me)
    ? '<p class="rank-note">Du erscheinst, sobald du in diesem Zeitraum etwas abgebaut hast.</p>'
    : '';

  const zeilen = data.rows.map((row, index) => {
    // Eine Luecke, wo der eigene Platz weit hinten angehaengt wurde — sonst
    // saehe „Platz 3, Platz 87" nach einem Sortierfehler aus.
    const vorher = data.rows[index - 1];
    const luecke = vorher && row.rank > vorher.rank + 1 ? '<div class="rank-gap">⋯</div>' : '';
    return luecke + rankRowHtml(row, board);
  }).join('');

  const stand = failed
    ? `Verbindung unterbrochen — Stand von vor ${agoStr(age)}`
    : `Stand von vor ${agoStr(age)} · ${fmt(data.total)} gewertet`;
  return `<div class="rank-rows">${zeilen}</div>${fehlt}` +
    `<p class="rank-note">${escapeHtml(stand)}</p>`;
}
